import {
  EndCustomer,
  PaymentInstrument,
} from "@api/graphql/generated/generated-types";
import {
  formatPaymentMethod,
  formatPaymentMethodSubtitle,
} from "@src/common/utils/paymentMethodFormatter";
import {
  CreditCardIcon,
  BuildingLibraryIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import CourieButton from "@src/common/components/Button/Button";
import { Tooltip } from "@src/common/components";
import WarningConfirmationModal from "@src/common/components/Modal/WarningConfirmationModal";
import { useState } from "react";
import { PaymentMethodRowActions } from "./PaymentMethodRowActions";

type PaymentMethodsListProps = {
  paymentMethods: PaymentInstrument[];
  onAddPaymentMethod?: () => void;
  onSelectPaymentMethod?: (method: PaymentInstrument) => void;
  onDeletePaymentMethod?: (paymentInstrumentId: string) => void;
  onUpdateDefaultPaymentMethod?: (
    method: PaymentInstrument,
    isDefault: boolean
  ) => void;
  loading?: boolean;
  error?: any;
  endCustomer?: EndCustomer;
  compact?: boolean; // For widget/narrow space usage
};

function PaymentMethodsList({
  paymentMethods,
  onAddPaymentMethod,
  onSelectPaymentMethod,
  onDeletePaymentMethod,
  onUpdateDefaultPaymentMethod,
  loading,
  error,
  endCustomer,
  compact = false,
}: PaymentMethodsListProps) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] =
    useState<PaymentInstrument | null>(null);

  const handleDeleteClick = (method: PaymentInstrument) => {
    setMethodToDelete(method);
    setDeleteModalOpen(true);
  };
  const handleConfirmDelete = () => {
    if (methodToDelete && onDeletePaymentMethod) {
      onDeletePaymentMethod(methodToDelete.id);
      setDeleteModalOpen(false);
      setMethodToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setMethodToDelete(null);
  };

  const getPaymentIcon = (type: string) => {
    return type === "CREDIT_CARD" ? (
      <CreditCardIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
    ) : (
      <BuildingLibraryIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
    );
  };

  return (
    <div
      className={`flex flex-col gap-4 ${compact ? "min-w-0" : "min-w-[600px]"}`}
    >
      <div className="flex justify-between items-center">
        <div className={compact ? "min-w-0 flex-1" : ""}>
          <h3
            className={`${
              compact ? "text-lg" : "text-xl"
            } font-semibold text-gray-800 dark:text-gray-100 ${compact ? "truncate" : ""}`}
          >
            Payment Methods
          </h3>
          {endCustomer?.companyName && (
            <p className={`text-sm text-gray-500 dark:text-gray-400 ${compact ? "truncate" : ""}`}>
              for {endCustomer.companyName}
            </p>
          )}
        </div>
        {onAddPaymentMethod && (
          <>
            {compact ? (
              <Tooltip content="Add Payment Method">
                <CourieButton onClick={onAddPaymentMethod} size="sm" pill>
                  <PlusIcon className="w-4 h-4" />
                </CourieButton>
              </Tooltip>
            ) : (
              <CourieButton onClick={onAddPaymentMethod} size="sm">
                <PlusIcon className="w-4 h-4" />
                Add Payment Method
              </CourieButton>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Loading payment methods...
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500 dark:text-red-400">
            Error loading payment methods
          </div>
        ) : paymentMethods?.length ? (
          paymentMethods.map((method) => {
            const isSelectable = !!onSelectPaymentMethod;
            const Element = isSelectable ? "button" : "div";

            return (
              <Element
                key={method.id}
                onClick={
                  isSelectable
                    ? () => onSelectPaymentMethod!(method)
                    : undefined
                }
                className={`flex flex-wrap items-center gap-4 p-4 border dark:border-gray-700 rounded-xl transition-colors group relative ${
                  isSelectable ? "hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" : ""
                } bg-white dark:bg-gray-800 ${compact ? "p-3" : "p-4"}`}
              >
                {!compact && getPaymentIcon(method.paymentInstrumentType)}
                {/* In the narrow panel the name keeps a base width, so the badge and buttons wrap below it instead of squeezing it */}
                <div
                  className={`flex flex-col items-start ${
                    compact ? "min-w-0 grow basis-24" : ""
                  }`}
                >
                  <span
                    className={`${
                      compact ? "text-sm" : "text-lg"
                    } font-medium text-gray-800 dark:text-gray-200 ${
                      compact ? "break-words w-full" : ""
                    }`}
                  >
                    {formatPaymentMethod(method)}
                  </span>
                  <span
                    className={`text-sm text-gray-500 dark:text-gray-400 ${
                      compact ? "text-xs break-words w-full" : ""
                    }`}
                  >
                    {formatPaymentMethodSubtitle(method)}
                  </span>
                </div>
                {method.isDefault && (
                  <span
                    className={`${
                      compact ? "relative" : "absolute top-2 right-2"
                    } text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-full ${
                      compact ? "flex-shrink-0" : ""
                    }`}
                  >
                    Default
                  </span>
                )}
                {isSelectable && !compact && (
                  <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-blue-600 dark:text-blue-400">Select →</span>
                  </div>
                )}
                <PaymentMethodRowActions
                  paymentMethod={method}
                  onUpdateDefaultPaymentMethod={onUpdateDefaultPaymentMethod}
                  onDeletePaymentMethod={
                    onDeletePaymentMethod ? handleDeleteClick : undefined
                  }
                />
              </Element>
            );
          })
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 mb-4">
              <span className="text-4xl mb-4 block">💳</span>
              <p className="text-lg dark:text-gray-200">No payment methods available</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Add a payment method for{" "}
                {endCustomer?.companyName || "this customer"} to process
                payments
              </p>
            </div>
          </div>
        )}
      </div>

      {deleteModalOpen && (
        <WarningConfirmationModal
          isModalOpen={deleteModalOpen}
          setIsModalOpen={setDeleteModalOpen}
          loading={!!loading}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          title="Delete Payment Method"
          textBody={`Are you sure you want to delete this payment method? This action cannot be undone.`}
          primaryButtonColor="failure"
          primaryButtonText="Delete"
          cancelButtonText="Cancel"
        />
      )}
    </div>
  );
}

export default PaymentMethodsList;
